import React, { type JSX } from "react";
import { useAppDispatch } from "../../hooks/reduxHooks";
import { setChatHistoryError } from "../../ChatSlice_Store";
import ChatMessageList from "./ChatMessageList";
import ChatActivity from "./ChatActivity";
import TextFeedbackBox from "./TextFeedbackBox";
import { ChatHistoryErrorBanner } from "./ChatOverlays";
import { useDocumentOpener, useStarterSelection, type UseCaseNotice } from "./useChatActions";
import type { ChatData } from "./useChatData";
import type { ChatScroll } from "../../hooks/useChatScroll";
import type { ChatFeedback } from "../../hooks/useChatFeedback";

export interface ChatTranscriptProps {
  chat: ChatData;
  useCase: UseCaseNotice;
  feedback: ChatFeedback;
  scroll: ChatScroll;
  inWorkspaceMode: boolean;
}

/** The scrollable message area: history errors, messages, feedback and status. */
export default function ChatTranscript({
  chat,
  useCase,
  feedback,
  scroll,
  inWorkspaceMode,
}: ChatTranscriptProps): JSX.Element {
  const dispatch = useAppDispatch();
  const onDocumentClick = useDocumentOpener(chat.allFacts);
  const onSelectStarter = useStarterSelection();

  return (
    <div
      ref={scroll.messagesContainerRef}
      className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4"
    >
      {chat.chatHistoryError && !chat.chatHistoryLoading && (
        <ChatHistoryErrorBanner
          chatHistoryError={chat.chatHistoryError}
          onDismiss={() => dispatch(setChatHistoryError(null))}
        />
      )}

      {!chat.chatHistoryLoading && (
        <ChatMessageList
          chatMessages={chat.chatMessages}
          allFacts={chat.allFacts}
          allDocuments={chat.allDocuments}
          onDocumentClick={onDocumentClick}
          feedback={feedback}
          selectedUseCase={useCase.selectedUseCase}
          showConversationStarters={chat.showConversationStarters}
          inWorkspaceMode={inWorkspaceMode}
          pillsDisabled={chat.pillsDisabled}
          onSelectStarter={onSelectStarter}
        />
      )}

      <TextFeedbackBox feedback={feedback} chatMessages={chat.chatMessages} />

      <ChatActivity chat={chat} />

      <div ref={scroll.bottomRef} />
    </div>
  );
}
